import React, { useEffect, useRef, useState } from 'react';
import { useJixxeoRecorder } from '../../context/RecorderContext';
import { Circle, Square, Pause, Play, Move, Mic, MicOff } from 'lucide-react';
import { formatDuration } from '../../lib/recordingsDb';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';

const POS_KEY = 'jixxeo_record_btn_pos';

export default function FloatingRecordButton() {
  const { user } = useAuth();
  const rec = useJixxeoRecorder();
  const loc = useLocation();
  const [pos, setPos] = useState(() => {
    try { return JSON.parse(localStorage.getItem(POS_KEY)) || null; } catch { return null; }
  });
  const [dragging, setDragging] = useState(false);
  const dragRef = useRef({ startX: 0, startY: 0, origX: 0, origY: 0, moved: false });
  const btnRef = useRef(null);

  // Hide on auth screens and on payment result pages
  const hideOn = ['/login', '/register', '/payment/success', '/payment/cancel'];
  if (!rec || !user || !rec.enabled || hideOn.some(p => loc.pathname.startsWith(p))) return null;

  const applyDefault = () => {
    if (pos) return pos;
    // default: bottom-right, 96px above bottom nav
    const w = 220, h = 56;
    const x = Math.max(12, (window.innerWidth || 400) - w - 16);
    const y = Math.max(12, (window.innerHeight || 800) - h - 96);
    return { x, y };
  };
  const p = applyDefault();

  const onPointerDown = (e) => {
    e.preventDefault();
    const rect = btnRef.current.getBoundingClientRect();
    dragRef.current = { startX: e.clientX, startY: e.clientY, origX: rect.left, origY: rect.top, moved: false };
    btnRef.current.setPointerCapture(e.pointerId);
    setDragging(true);
  };
  const onPointerMove = (e) => {
    if (!dragging) return;
    const dx = e.clientX - dragRef.current.startX;
    const dy = e.clientY - dragRef.current.startY;
    if (Math.abs(dx) + Math.abs(dy) > 4) dragRef.current.moved = true;
    const w = btnRef.current.offsetWidth, h = btnRef.current.offsetHeight;
    const nx = Math.min(Math.max(4, dragRef.current.origX + dx), (window.innerWidth || 400) - w - 4);
    const ny = Math.min(Math.max(4, dragRef.current.origY + dy), (window.innerHeight || 800) - h - 4);
    setPos({ x: nx, y: ny });
  };
  const onPointerUp = (e) => {
    setDragging(false);
    if (dragRef.current.moved) {
      localStorage.setItem(POS_KEY, JSON.stringify(pos || applyDefault()));
    } else {
      // treat as click
      handleClick();
    }
  };

  const handleClick = async () => {
    if (rec.state === 'recording' || rec.state === 'paused') {
      await rec.stopAndPreview();
    } else if (rec.state === 'starting' || rec.state === 'processing') {
      // ignore
    } else {
      // Always open the picker so mobile users can choose an available mode.
      rec.setShowPicker(true);
    }
  };

  const isRec = rec.state === 'recording' || rec.state === 'paused';
  const starting = rec.state === 'starting' || rec.state === 'processing';

  return (
    <div
      ref={btnRef}
      data-testid="floating-record-button"
      role="button"
      aria-label={isRec ? 'Stop recording' : 'Record'}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      style={{ left: p.x, top: p.y }}
      className={`fixed z-[60] select-none touch-none no-tap-highlight shadow-2xl rounded-full pl-3 pr-4 py-2.5 flex items-center gap-2 cursor-grab active:cursor-grabbing backdrop-blur-xl border transition-colors
        ${isRec ? 'bg-red-600 border-red-400 text-white shadow-red-500/40' : 'bg-slate-900/85 border-white/15 text-white shadow-violet-500/25 hover:bg-slate-900'}`}
    >
      <Move className="w-3.5 h-3.5 opacity-50" data-testid="record-btn-drag-handle" />
      {isRec ? (
        <>
          <span className="relative flex h-3 w-3">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-300 opacity-75" />
            <span className="relative inline-flex rounded-full h-3 w-3 bg-white" />
          </span>
          <span className="font-mono font-bold text-sm tabular-nums" data-testid="record-timer">{formatDuration(rec.elapsed)}</span>
          <span className="text-xs uppercase tracking-wider opacity-80">
            {rec.state === 'paused' ? 'Paused' : 'REC'}
          </span>
          <Square className="w-4 h-4 ml-1" />
        </>
      ) : starting ? (
        <>
          <Circle className="w-4 h-4 animate-pulse" />
          <span className="text-sm font-semibold">{rec.state === 'processing' ? 'Saving…' : 'Starting…'}</span>
        </>
      ) : (
        <>
          <span className="w-3 h-3 rounded-full bg-red-500 shadow-inner shadow-red-900" />
          <span className="text-sm font-semibold">Record</span>
        </>
      )}
    </div>
  );
}
